import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import type { WeeklyGoal } from '@/types';
import { WeeklyGoalItem } from './WeeklyGoalItem';
import styles from '@/pages/WeeklyPlanningPage.module.css';

export interface WeeklyGoalsSectionProps {
  goals: WeeklyGoal[];
  onAddGoal: (title: string) => void;
  onToggleGoal: (id: string) => void;
  onRemoveGoal: (id: string) => void;
  onEditGoal: (id: string, title: string) => void;
}

export function WeeklyGoalsSection({
  goals,
  onAddGoal,
  onToggleGoal,
  onRemoveGoal,
  onEditGoal,
}: WeeklyGoalsSectionProps) {
  const [newGoalTitle, setNewGoalTitle] = useState('');
  const completedCount = goals.filter((g) => g.completed).length;

  const handleAddGoal = () => {
    if (newGoalTitle.trim() && goals.length < 5) {
      onAddGoal(newGoalTitle.trim());
      setNewGoalTitle('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleAddGoal();
    }
  };

  return (
    <section className={styles.goalsSection}>
      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle}>Weekly Objectives</h2>
        <span className={styles.goalsProgress}>
          {completedCount}/{goals.length} complete
        </span>
      </div>

      <div className={styles.goalsList}>
        <AnimatePresence mode="popLayout">
          {goals.map((goal) => (
            <WeeklyGoalItem
              key={goal.id}
              goal={goal}
              onToggle={onToggleGoal}
              onRemove={onRemoveGoal}
              onEdit={onEditGoal}
            />
          ))}
        </AnimatePresence>

        {goals.length < 5 && (
          <div className={styles.addGoalRow}>
            <input
              type="text"
              className={styles.addGoalInput}
              placeholder="Add a weekly objective..."
              value={newGoalTitle}
              onChange={(e) => setNewGoalTitle(e.target.value)}
              onKeyDown={handleKeyDown}
              maxLength={100}
            />
            <motion.button
              className={styles.addGoalBtn}
              onClick={handleAddGoal}
              disabled={!newGoalTitle.trim()}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Add
            </motion.button>
          </div>
        )}
      </div>

      {goals.length === 0 && (
        <p className={styles.emptyHint}>
          Set 3-5 objectives to focus your week. What matters most?
        </p>
      )}
    </section>
  );
}
